import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekday);
dayjs.extend(relativeTime);

export function calculateAge(date: Date): number {
  if (!dayjs(date).isValid()) {
    return 0;
  }

  const today = new Date();
  const dateFrom = dayjs(date);
  let age = today.getFullYear() - dateFrom.year();
  var m = today.getMonth() - dateFrom.month();
  if (m < 0 || (m === 0 && today.getDate() < dateFrom.day())) {
    age--;
  }
  return age;
}

export function timeFrom(date: Date, withoutSuffix: boolean) {
  dayjs.extend(relativeTime);
  if (dayjs(date).isValid()) {
    return dayjs(date).fromNow(true);
  }

  return '';
}

export function timeTo(date: Date) {
  dayjs.extend(relativeTime);
  if (dayjs(date).isValid()) {
    return dayjs().to(dayjs(date));
  }

  return '';
}

export function formatDate(date: Date | string, format: string) {
  if (dayjs(date).isValid()) {
    return dayjs(date).format(format);
  }
}

export function createDate(date: string, format: string) {
  if (dayjs(date, format).isValid()) {
    return dayjs(date, format);
  }
}

export function isToday(date: Date) {
  if (
    dayjs(date).isValid() &&
    dayjs(date).day() === dayjs().day() &&
    dayjs(date).month() === dayjs().month() &&
    dayjs(date).year() === dayjs().year()
  ) {
    return true;
  }
  return false;
}

export function isTheSameDay(
  firstDate: Date | string,
  secondDate: Date | string
) {
  if (dayjs(firstDate).isValid() && dayjs(secondDate).isValid()) {
    const date1 = dayjs(firstDate);
    const date2 = dayjs(secondDate);
    return date1.isSame(date2, 'day');
  }
  return false;
}

export function isBefore(firstDate: Date, secondDate: Date) {
  if (dayjs(firstDate).isValid() && dayjs(secondDate).isValid()) {
    return dayjs(firstDate).isBefore(dayjs(secondDate));
  }

  return false;
}

export function isAfterNow(date: Date) {
  if (dayjs(date).isValid()) {
    return dayjs(date).isAfter(dayjs());
  }

  return false;
}

export function isBeforeNow(date: Date | string) {
  if (dayjs(date).isValid()) {
    return dayjs(date).isBefore(dayjs());
  }

  return false;
}

export function inTheFuture(date: Date | string) {
  if (dayjs(date).isValid()) {
    return dayjs(date).isAfter(dayjs());
  }

  return false;
}

export function checkSameOrBefore(
  currentDate: Date | string,
  endDate: Date | string
) {
  return (
    dayjs(currentDate).isValid() && dayjs(currentDate).isSameOrBefore(endDate)
  );
}

export function checkSameOrAfter(
  currentDate: Date | string,
  endDate: Date | string
) {
  return (
    dayjs(currentDate).isValid() && dayjs(currentDate).isSameOrAfter(endDate)
  );
}

export function isBetween(
  date: Date | string,
  startDate: Date | string,
  endDate: Date | string,
  inclusive: boolean = true
): boolean {
  dayjs.extend(isSameOrAfter);
  dayjs.extend(isSameOrBefore);
  if (
    !dayjs(date).isValid() ||
    !dayjs(startDate).isValid() ||
    !dayjs(endDate).isValid()
  ) {
    return false;
  }

  const dateToCheck = dayjs(date).startOf('day');
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).endOf('day');

  if (start.isAfter(end)) {
    return false;
  }

  if (inclusive) {
    return dateToCheck.isSameOrAfter(start) && dateToCheck.isSameOrBefore(end);
  } else {
    return dateToCheck.isAfter(start) && dateToCheck.isBefore(end);
  }
}

export function addMinutes(date: Date, mins: number) {
  if (dayjs(date).isValid()) {
    return dayjs(date).add(mins, 'm').toDate();
  }
}

export function returnIfValid(date: string) {
  if (dayjs(date).isValid()) {
    return dayjs(date).toDate();
  }
}

export function startOfWeek(date?: Date) {
  if (dayjs(date).isValid()) {
    return dayjs(date).startOf('isoWeek').toDate();
  }

  dayjs.extend(weekday);
  return dayjs().day(1).startOf('day').toDate();
}

export function endOfWeek(date?: Date) {
  if (dayjs(date).isValid()) {
    return dayjs(date).endOf('isoWeek').toDate();
  }

  dayjs.extend(weekday);
  return dayjs().day(1).add(6, 'day').endOf('day').toDate();
}

export function endOfMonth() {
  dayjs.extend(weekday);
  return dayjs().day(1).add(1, 'month').endOf('day').toDate();
}

export function todayFormattedForMinMax() {
  return dayjs().format('YYYY-MM-DD');
}

export function dayAndNumber(date: Date) {
  return dayjs(date).format('ddd D');
}

export function dayAndNumberAndMonth(date: Date) {
  return dayjs(date).format('ddd D MMM');
}

export function addDays(date: Date, number: number) {
  return dayjs(date).add(number, 'day').toDate();
}

export function subtractDays(date: Date, number: number) {
  return dayjs(date).subtract(number, 'day').toDate();
}

export function getDaysBetween(startDate: Date, endDate: Date) {
  let currentDate = dayjs(startDate);
  const end = dayjs(endDate);
  const dates = [];

  while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
    dates.push(currentDate.toDate());
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

export function moveWeek(
  date: string | Date,
  direction: 'forward' | 'backward'
) {
  return direction === 'forward'
    ? dayjs(date).add(1, 'week').toDate()
    : dayjs(date).subtract(1, 'week').toDate();
}
